import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { creatorGroupsService, type CreatorGroup, type GroupMember } from '../services/creatorGroupsService';
import './CreatorGroupsPage.css';

const CreatorGroupsPage: React.FC = () => {
  const { t } = useTranslation(['creator', 'common']);

  // Groups list
  const [groups, setGroups] = useState<CreatorGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Create/edit form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CreatorGroup | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  // Selected group for member management
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [pendingMembers, setPendingMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberInput, setAddMemberInput] = useState('');
  const [addMemberResult, setAddMemberResult] = useState<{ added: string[]; notFound: string[]; duplicates: string[] } | null>(null);

  // Delete confirmation
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      const data = await creatorGroupsService.listGroups();
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadMembers = useCallback(async (groupId: string) => {
    setMembersLoading(true);
    try {
      const [approved, pending] = await Promise.all([
        creatorGroupsService.listMembers(groupId, 'approved'),
        creatorGroupsService.listMembers(groupId, 'pending'),
      ]);
      setMembers(approved);
      setPendingMembers(pending);
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      loadMembers(selectedGroupId);
    }
  }, [selectedGroupId, loadMembers]);

  // ─── Handlers ─────────────────────────────────────────────────

  const handleCreateOrUpdate = async () => {
    if (!formName.trim()) return;
    setFormSaving(true);
    try {
      if (editingGroup) {
        await creatorGroupsService.updateGroup(editingGroup.id, {
          name: formName,
          description: formDescription || undefined,
        });
      } else {
        await creatorGroupsService.createGroup(formName, formDescription || undefined);
      }
      setShowCreateForm(false);
      setEditingGroup(null);
      setFormName('');
      setFormDescription('');
      await loadGroups();
    } catch (error) {
      console.error('Failed to save group:', error);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (groupId: string) => {
    try {
      await creatorGroupsService.deleteGroup(groupId);
      setDeletingGroupId(null);
      if (selectedGroupId === groupId) {
        setSelectedGroupId(null);
      }
      await loadGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedGroupId || !addMemberInput.trim()) return;
    const emails = addMemberInput
      .split(/[,;\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    if (emails.length === 0) return;

    try {
      const result = await creatorGroupsService.addMembers(selectedGroupId, emails);
      setAddMemberResult(result);
      setAddMemberInput('');
      await loadMembers(selectedGroupId);
    } catch (error) {
      console.error('Failed to add members:', error);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroupId) return;
    try {
      await creatorGroupsService.removeMember(selectedGroupId, userId);
      await loadMembers(selectedGroupId);
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const handleApproveReject = async (userId: string, status: 'approved' | 'rejected') => {
    if (!selectedGroupId) return;
    try {
      await creatorGroupsService.updateMemberStatus(selectedGroupId, userId, status);
      await loadMembers(selectedGroupId);
    } catch (error) {
      console.error('Failed to update member status:', error);
    }
  };

  const startEdit = (group: CreatorGroup) => {
    setEditingGroup(group);
    setFormName(group.name);
    setFormDescription(group.description || '');
    setShowCreateForm(true);
  };

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  if (loading) {
    return (
      <div className="creator-groups-page">
        <div className="loading-state">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="creator-groups-page">
      <header className="page-header">
        <div>
          <h1>{t('creator:groups.title')}</h1>
          <p>{t('creator:groups.subtitle')}</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingGroup(null);
            setFormName('');
            setFormDescription('');
            setShowCreateForm(true);
          }}
        >
          {t('creator:groups.createGroup')}
        </button>
      </header>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editingGroup ? t('creator:groups.editGroup') : t('creator:groups.createGroup')}</h3>
            <div className="form-group">
              <label>{t('creator:groups.groupName')}</label>
              <input
                type="text"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder={t('creator:groups.groupNamePlaceholder')}
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>{t('creator:groups.groupDescription')}</label>
              <textarea
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder={t('creator:groups.groupDescriptionPlaceholder')}
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateForm(false)}>
                {t('common:cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateOrUpdate}
                disabled={!formName.trim() || formSaving}
              >
                {formSaving ? t('common:saving') : editingGroup ? t('common:save') : t('creator:groups.createGroup')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="groups-layout">
        {/* Groups List */}
        <div className="groups-list-panel">
          <h3>{t('creator:groups.yourGroups')}</h3>
          {groups.length === 0 ? (
            <div className="empty-state">
              <p>{t('creator:groups.noGroups')}</p>
            </div>
          ) : (
            <div className="groups-list">
              {groups.map(group => (
                <div
                  key={group.id}
                  className={`group-card ${selectedGroupId === group.id ? 'selected' : ''}`}
                  onClick={() => setSelectedGroupId(group.id)}
                >
                  <div className="group-card-header">
                    <h4>{group.name}</h4>
                    <span className="member-badge">{group.memberCount || 0}</span>
                  </div>
                  {group.description && <p className="group-desc">{group.description}</p>}
                  <div className="group-card-actions">
                    <button className="btn-text" onClick={(e) => { e.stopPropagation(); startEdit(group); }}>
                      {t('common:edit')}
                    </button>
                    {deletingGroupId === group.id ? (
                      <>
                        <span className="delete-confirm-text">{t('creator:groups.confirmDelete')}</span>
                        <button className="btn-text btn-danger" onClick={(e) => { e.stopPropagation(); handleDelete(group.id); }}>
                          {t('common:confirm')}
                        </button>
                        <button className="btn-text" onClick={(e) => { e.stopPropagation(); setDeletingGroupId(null); }}>
                          {t('common:cancel')}
                        </button>
                      </>
                    ) : (
                      <button className="btn-text btn-danger" onClick={(e) => { e.stopPropagation(); setDeletingGroupId(group.id); }}>
                        {t('common:delete')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Member Management Panel */}
        <div className="members-panel">
          {!selectedGroupId ? (
            <div className="empty-state">
              <p>{t('creator:groups.selectGroupToManage')}</p>
            </div>
          ) : (
            <>
              <h3>{selectedGroup?.name} - {t('creator:groups.members')}</h3>

              {/* Pending Requests */}
              {pendingMembers.length > 0 && (
                <div className="pending-section">
                  <h4>{t('creator:groups.pendingRequests')} ({pendingMembers.length})</h4>
                  <div className="members-list">
                    {pendingMembers.map(member => (
                      <div key={member.id} className="member-item pending">
                        <div className="member-info">
                          <span className="member-name">{member.user?.fullName || member.user?.username || member.user?.email}</span>
                          <span className="member-email">{member.user?.email}</span>
                        </div>
                        <div className="member-actions">
                          <button className="btn-small btn-approve" onClick={() => handleApproveReject(member.userId, 'approved')}>
                            {t('creator:groups.approve')}
                          </button>
                          <button className="btn-small btn-reject" onClick={() => handleApproveReject(member.userId, 'rejected')}>
                            {t('creator:groups.reject')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Members */}
              <div className="add-members-section">
                <h4>{t('creator:groups.addMembers')}</h4>
                <div className="add-members-row">
                  <input
                    type="text"
                    value={addMemberInput}
                    onChange={e => setAddMemberInput(e.target.value)}
                    placeholder={t('creator:groups.addMembersPlaceholder')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddMembers();
                      }
                    }}
                  />
                  <button className="btn-primary btn-small" onClick={handleAddMembers}>
                    {t('common:add')}
                  </button>
                </div>
                {addMemberResult && (
                  <div className="add-result">
                    {addMemberResult.added.length > 0 && (
                      <p className="result-success">{t('creator:groups.membersAdded', { count: addMemberResult.added.length })}</p>
                    )}
                    {addMemberResult.notFound.length > 0 && (
                      <p className="result-warning">{t('creator:groups.emailsNotFound')}: {addMemberResult.notFound.join(', ')}</p>
                    )}
                    {addMemberResult.duplicates.length > 0 && (
                      <p className="result-info">{t('creator:groups.alreadyMembers')}: {addMemberResult.duplicates.join(', ')}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Approved Members */}
              <div className="approved-section">
                <h4>{t('creator:groups.approvedMembers')} ({members.length})</h4>
                {membersLoading ? (
                  <p className="loading-text">{t('common:loading')}</p>
                ) : members.length === 0 ? (
                  <p className="empty-text">{t('creator:groups.noMembers')}</p>
                ) : (
                  <div className="members-list">
                    {members.map(member => (
                      <div key={member.id} className="member-item">
                        <div className="member-info">
                          <span className="member-name">{member.user?.fullName || member.user?.username || member.user?.email}</span>
                          <span className="member-email">{member.user?.email}</span>
                        </div>
                        <button className="btn-text btn-danger btn-small" onClick={() => handleRemoveMember(member.userId)}>
                          {t('common:remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorGroupsPage;
